import { Container, Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link, useParams, useLoaderData } from 'react-router';

import Auth from '../../../api/auth';
import BreadCrumb from '../../../components/BreadCrumb';
import GitlabWikiHelpButton from '../../../components/GitlabWikiHelpButton';
import OverviewTable from '../../../components/OverviewTable/OverviewTable';
import { assignableRoleMessageIds, roleMessageId } from '../../../helpers/lookups';
import useApiQuery from '../../../hooks/useApiQuery';
import rbacCheck from '../../../rbac/rbacCheck';

import DeleteUserFormModal from './DeleteUserFormModal';
import EditUserFormModal from './EditUserFormModal';

import { useUserData } from '@/redux/selectors';
import type { ErrorType } from '@/types';
import type { Row } from '@/types/tableTypes';
import type { User } from '@/types/user';

const usersGuideSlug = 'user-guide/organizations/users/details';

const UserDetails = () => {
  const intl = useIntl();
  const { uuid } = useParams() as { uuid: string };
  const user = useUserData();

  const loaderUser: User = useLoaderData();
  const [userDetails, setUserDetails] = useState(loaderUser);
  const [editModalOpened, setEditModalOpened] = useState(false);
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);

  const {
    data: createdBy,
    error: fetchCreatedByError,
    makeApiCall: getUserCall,
  } = useApiQuery<User[]>(Auth.fetchV2Users);
  const { makeApiCall: getUserDetailsCall } = useApiQuery<User>(
    Auth.fetchV2UserByUuid as (...args: unknown[]) => Promise<User | { data?: User }>
  );

  const userRoles = rbacCheck(
    user.roles,
    '/users:PUT_ROLE_CHECK',
    userDetails.organization?.enterpriseName
  ) as string[];

  const canEditRole = rbacCheck(user.roles, '/users:PUT', {
    userToBeUpdatedRole: userDetails.role,
    userToBeUpdatedEmail: userDetails.email,
    userEmail: user.email,
  });
  const canSeeOrganizations = rbacCheck(user.roles, '/organizations:VIEW_ALL');
  const canEditOrg = rbacCheck(user.roles, '/users:PUT:ORG');
  const canDeleteUser = rbacCheck(user.roles, '/users:DELETE', {
    userToBeDeletedRole: userDetails.role,
    userToBeDeletedEmail: userDetails.email,
    userEmail: user.email,
  });

  const getUserDetails = async () => {
    try {
      const data = await getUserDetailsCall(uuid);
      if (data) setUserDetails(data);
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
  };

  useEffect(() => {
    const getCreatedBy = async () => {
      await getUserCall({ email: userDetails.createdByEmail });
    };

    getCreatedBy();
  }, [getUserCall, userDetails]);

  const rows: Row[] = [
    {
      title: { id: 'role' },
      value: intl.formatMessage({ id: roleMessageId(userDetails.role) }),
    },
    {
      title: { id: 'organization' },
      component: (
        <Link to={`/governance/organizations/${userDetails.organizationId}`}>
          {userDetails.organization?.enterpriseName}
        </Link>
      ),
    },
    {
      title: { id: 'createdBy' },
      component: createdBy?.[0] ? (
        <Link
          to={`/governance/organizations/${userDetails.organizationId}/users/${createdBy[0].uuid}`}
        >
          {createdBy[0].email}
        </Link>
      ) : (
        userDetails.createdByEmail
      ),
      error: fetchCreatedByError as ErrorType,
    },
  ];

  return (
    <>
      {editModalOpened && (
        <EditUserFormModal
          roles={Object.fromEntries(
            Object.entries(assignableRoleMessageIds(userRoles)).map(([key, id]) => [
              key,
              intl.formatMessage({ id }),
            ])
          )}
          user={userDetails}
          canEditOrg={canEditOrg as boolean}
          onSubmit={() => {
            getUserDetails();
          }}
          onCancel={() => {
            setEditModalOpened(false);
          }}
        />
      )}
      {deleteModalOpened && (
        <DeleteUserFormModal
          user={userDetails}
          onSubmit={() => {
            setDeleteModalOpened(false);
          }}
          onCancel={() => {
            setDeleteModalOpened(false);
          }}
        />
      )}
      <Grid
        container
        className='content-with-header'
        direction='column'
        rowGap={4}
      >
        <Grid
          item
          alignSelf='flex-start'
        >
          <BreadCrumb
            items={[
              {
                href: canSeeOrganizations
                  ? '/governance/organizations'
                  : `/governance/organizations/${userDetails.organizationId}`,
                text: intl.formatMessage({ id: 'organizations' }),
              },
              {
                href: `/governance/organizations/${userDetails.organizationId}`,
                text: userDetails.organization?.enterpriseName ?? '',
              },
              {
                href: `/governance/organizations/${userDetails.organizationId}/users`,
                text: intl.formatMessage({ id: 'users' }),
              },
              {
                href: '#',
                text: userDetails.email,
              },
            ]}
          />
        </Grid>
        <Grid item>
          <Container>
            <OverviewTable
              header={userDetails.email}
              rows={rows}
              editAction={{
                onClick: () => {
                  setEditModalOpened(true);
                },
                disabled: !canEditRole,
                tooltip: 'userEditPermissionErr',
                label: '',
              }}
              deleteAction={{
                onClick: () => {
                  setDeleteModalOpened(true);
                },
                disabled: !canDeleteUser,
                tooltip: 'userDeletePermissionErr',
                label: '',
              }}
              trailingActionSlot={
                <GitlabWikiHelpButton
                  slug={usersGuideSlug}
                  tooltipMessageId='usersGuide'
                  tooltipDefaultMessage='Users guide'
                  modalTitleMessageId='usersGuide'
                  modalTitleDefaultMessage='Users guide'
                  buttonAriaLabel={intl.formatMessage({ id: 'usersGuideAriaLabel' })}
                  buttonSx={{ mt: 0 }}
                />
              }
            />
          </Container>
        </Grid>
      </Grid>
    </>
  );
};

export default UserDetails;
